'use client';

import { useId } from 'react';

/**
 * Listing-type badges rendered on the top-right of an
 * `AllProductsCard`. 1:1 port of legacy
 * `flows/pdp/components/{DigitalProductBadge,MeetingSlotBadge}`
 * SVGs — same gradients, same paths.
 *
 * Each badge is a 22×22 rounded square with a gradient fill
 * and a white glyph at the centre.
 */

interface IconProps {
  size?: number;
  className?: string;
}

export function DigitalProductBadge({ size = 16, className }: IconProps) {
  // SSR-safe unique id per render — keeps two badges on the
  // same page from sharing one `<linearGradient>` definition.
  const gradientId = useId().replace(/[^A-Za-z0-9_-]/g, '');
  const href = `url(#${gradientId})`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      className={className}
      aria-hidden
    >
      <rect width="22" height="22" rx="11" fill={href} />
      <path
        d="M16.2852 8.18276L12.83 4.72763C12.7842 4.68181 12.7297 4.64547 12.6698 4.6207C12.6099 4.59593 12.5457 4.5832 12.4808 4.58325H6.55774C6.29592 4.58325 6.04483 4.68726 5.85969 4.87239C5.67456 5.05752 5.57056 5.30862 5.57056 5.57043V16.4294C5.57056 16.6912 5.67456 16.9423 5.85969 17.1274C6.04483 17.3126 6.29592 17.4166 6.55774 17.4166H15.4424C15.7042 17.4166 15.9553 17.3126 16.1404 17.1274C16.3255 16.9423 16.4295 16.6912 16.4295 16.4294V8.53197C16.4296 8.46713 16.4169 8.40292 16.3921 8.343C16.3673 8.28308 16.331 8.22863 16.2852 8.18276ZM12.9744 6.26824L14.7445 8.03838H12.9744V6.26824ZM15.4424 16.4294H6.55774V5.57043H11.9872V8.53197C11.9872 8.66288 12.0392 8.78842 12.1318 8.88099C12.2244 8.97356 12.3499 9.02556 12.4808 9.02556H15.4424V16.4294ZM12.83 12.6251C12.8759 12.6709 12.9123 12.7253 12.9372 12.7853C12.962 12.8452 12.9748 12.9094 12.9748 12.9743C12.9748 13.0391 12.962 13.1034 12.9372 13.1633C12.9123 13.2232 12.8759 13.2777 12.83 13.3235L11.3493 14.8043C11.3034 14.8502 11.249 14.8866 11.1891 14.9114C11.1291 14.9362 11.0649 14.949 11 14.949C10.9352 14.949 10.8709 14.9362 10.811 14.9114C10.7511 14.8866 10.6967 14.8502 10.6508 14.8043L9.17006 13.3235C9.07744 13.2309 9.02541 13.1053 9.02541 12.9743C9.02541 12.8433 9.07744 12.7177 9.17006 12.6251C9.26268 12.5324 9.38829 12.4804 9.51927 12.4804C9.65026 12.4804 9.77587 12.5324 9.86849 12.6251L10.5065 13.2636V10.5063C10.5065 10.3754 10.5585 10.2499 10.651 10.1573C10.7436 10.0647 10.8691 10.0127 11 10.0127C11.131 10.0127 11.2565 10.0647 11.3491 10.1573C11.4416 10.2499 11.4936 10.3754 11.4936 10.5063V13.2636L12.1316 12.6251C12.1774 12.5792 12.2319 12.5428 12.2918 12.5179C12.3517 12.4931 12.4159 12.4803 12.4808 12.4803C12.5457 12.4803 12.6099 12.4931 12.6698 12.5179C12.7297 12.5428 12.7842 12.5792 12.83 12.6251Z"
        fill="white"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="11"
          x2="22"
          y2="11"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#DB3DCA" />
          <stop offset="1" stopColor="#7286E6" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function MeetingSlotBadge({ size = 16, className }: IconProps) {
  const gradientId = useId().replace(/[^A-Za-z0-9_-]/g, '');
  const href = `url(#${gradientId})`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      className={className}
      aria-hidden
    >
      <rect width="22" height="22" rx="11" fill={href} />
      <path
        d="M15.9361 5.57049H14.4554V5.0769C14.4554 4.94599 14.4034 4.82045 14.3108 4.72788C14.2182 4.63532 14.0927 4.58331 13.9618 4.58331C13.8309 4.58331 13.7053 4.63532 13.6127 4.72788C13.5202 4.82045 13.4682 4.94599 13.4682 5.0769V5.57049H8.53228V5.0769C8.53228 4.94599 8.48027 4.82045 8.38771 4.72788C8.29514 4.63532 8.1696 4.58331 8.03869 4.58331C7.90778 4.58331 7.78223 4.63532 7.68967 4.72788C7.5971 4.82045 7.5451 4.94599 7.5451 5.0769V5.57049H6.06433C5.80251 5.57049 5.55142 5.6745 5.36629 5.85963C5.18115 6.04476 5.07715 6.29586 5.07715 6.55767V16.4295C5.07715 16.6913 5.18115 16.9424 5.36629 17.1275C5.55142 17.3126 5.80251 17.4166 6.06433 17.4166H15.9361C16.1979 17.4166 16.449 17.3126 16.6342 17.1275C16.8193 16.9424 16.9233 16.6913 16.9233 16.4295V6.55767C16.9233 6.29586 16.8193 6.04476 16.6342 5.85963C16.449 5.6745 16.1979 5.57049 15.9361 5.57049ZM7.5451 6.55767V7.05126C7.5451 7.18217 7.5971 7.30772 7.68967 7.40028C7.78223 7.49285 7.90778 7.54485 8.03869 7.54485C8.1696 7.54485 8.29514 7.49285 8.38771 7.40028C8.48027 7.30772 8.53228 7.18217 8.53228 7.05126V6.55767H13.4682V7.05126C13.4682 7.18217 13.5202 7.30772 13.6127 7.40028C13.7053 7.49285 13.8309 7.54485 13.9618 7.54485C14.0927 7.54485 14.2182 7.49285 14.3108 7.40028C14.4034 7.30772 14.4554 7.18217 14.4554 7.05126V6.55767H15.9361V8.53203H6.06433V6.55767H7.5451ZM15.9361 16.4295H6.06433V9.51921H15.9361V16.4295Z"
        fill="white"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="11"
          x2="22"
          y2="11"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4768FF" />
          <stop offset="1" stopColor="#20EE4D" />
        </linearGradient>
      </defs>
    </svg>
  );
}
